import { File, FileImage, FileText, RotateCcw } from 'lucide-react';
import { attachmentKind, fileSize, fileType, type Attachment } from '@/lib/attachments';
import { Button } from './Button';
import { cn } from './cn';

export function FileChip({
  file,
  onOpen,
  onOpenFrom,
  onRetry,
  firstLineBaseline = false,
}: {
  file: Attachment;
  /** Expose the filename's first baseline to property rows. */
  firstLineBaseline?: boolean;
  onOpen?: () => void;
  /** Opt-in: return focus to the source button after opening a preview panel. */
  onOpenFrom?: (trigger: HTMLButtonElement) => void;
  onRetry?: () => void;
}) {
  const kind = attachmentKind(file.mime);
  const Icon = kind === 'image' ? FileImage : kind === 'pdf' ? FileText : File;
  const progress = Math.min(100, Math.max(0, file.progress ?? 0));
  return (
    <div
      data-slot="file-chip"
      data-file-id={file.id}
      data-state={file.state}
      className={cn(
        'min-w-0 rounded-md border border-line bg-card',
        firstLineBaseline && 'inline-flex w-full flex-col',
      )}
    >
      <div
        className={
          firstLineBaseline ? 'flex min-w-0 items-baseline gap-3 p-3' : 'flex min-w-0 items-center gap-3 p-3'
        }
      >
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-sm bg-muted text-fg-muted',
            firstLineBaseline && 'self-center',
          )}
        >
          <Icon className="size-5" aria-hidden />
        </span>
        <div className={firstLineBaseline ? 'flex min-w-0 flex-1 flex-col items-start' : 'min-w-0 flex-1'}>
          {file.state === 'ready' && (onOpen || onOpenFrom) ? (
            <button
              type="button"
              onClick={onOpenFrom ? (event) => onOpenFrom(event.currentTarget) : onOpen}
              title={file.name}
              className="block max-w-full truncate rounded-xs text-left text-sm font-medium text-fg hover:text-brand-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              {file.name}
            </button>
          ) : (
            <p className="truncate text-sm font-medium" title={file.name}>
              {file.name}
            </p>
          )}
          <p className="mt-1 text-xs text-fg-muted">
            {fileType(file.name, file.mime)} · {fileSize(file.size)}
          </p>
        </div>
        {file.state === 'error' && onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            <RotateCcw className="size-3.5" />
            Повторить
          </Button>
        )}
      </div>
      {file.state === 'uploading' && (
        <div className="px-3 pb-3">
          <div className="mb-2 flex justify-between text-xs text-fg-muted">
            <span>{progress < 60 ? 'Добавление файла' : 'Проверка файла'}</span>
            <span>{progress}%</span>
          </div>
          <div
            role="progressbar"
            aria-label={file.name}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            className="h-1 overflow-hidden rounded-full bg-muted"
          >
            <div
              className="h-full bg-info transition-all duration-normal motion-reduce:transition-none"
              style={{ width: progress + '%' }}
            />
          </div>
        </div>
      )}
      {file.state === 'error' && (
        <p role="alert" className="px-3 pb-3 text-xs text-danger-fg">
          {file.error ?? 'Не удалось добавить файл'}
        </p>
      )}
    </div>
  );
}
